import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressCritiqueReviewsReviewsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/reviews',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/reviews',
        destPath: '/api',
      },
      {
        srcPath: '/_api/reviews/api/v1/reviews',
        destPath: '/api/v1/reviews',
      },
      {
        srcPath: '/_api/reviews/v1/reviews',
        destPath: '/v1/reviews',
      },
      {
        srcPath: '/reviews/v1/bulk/reviews',
        destPath: '/v1/bulk/reviews',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/reviews',
        destPath: '/api',
      },
      {
        srcPath: '/reviews/api/v1/reviews',
        destPath: '/api/v1/reviews',
      },
      {
        srcPath: '/reviews/v1/reviews',
        destPath: '/v1/reviews',
      },
      {
        srcPath: '/reviews/v1/bulk/reviews',
        destPath: '/v1/bulk/reviews',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/reviews',
        destPath: '/api',
      },
      {
        srcPath: '/_api/reviews/api/v1/reviews',
        destPath: '/api/v1/reviews',
      },
      {
        srcPath: '/_api/reviews/v1/reviews',
        destPath: '/v1/reviews',
      },
      {
        srcPath: '/_api/reviews/bulk/v1/reviews',
        destPath: '/v1/bulk/reviews',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/reviews',
        destPath: '/api',
      },
      {
        srcPath: '/_api/reviews/api/v1/reviews',
        destPath: '/api/v1/reviews',
      },
      {
        srcPath: '/_api/reviews/v1/reviews',
        destPath: '/v1/reviews',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/reviews',
        destPath: '/api',
      },
      {
        srcPath: '/_api/reviews/api/v1/reviews',
        destPath: '/api/v1/reviews',
      },
      {
        srcPath: '/_api/reviews/v1/reviews',
        destPath: '/v1/reviews',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/reviews/v1/reviews',
        destPath: '/v1/reviews',
      },
      {
        srcPath: '/reviews/v1/bulk/reviews',
        destPath: '/v1/bulk/reviews',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_reviews_reviews';

/**
 * Retrieves a review.
 *
 * By default, unpublished reviews aren't returned.
 * To retrieve an unpublished review, pass `returnPrivateReviews` as `true`.
 */
export function getReview(payload: object): RequestOptionsFactory<any> {
  function __getReview({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.GetReview',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/reviews/{reviewId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'review.createdDate' },
              { path: 'review.updatedDate' },
              { path: 'review.reviewDate' },
              { path: 'review.reply.createdDate' },
              { path: 'review.reply.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'review.relevanceScore' }],
          },
        ]),
      fallback: [
        {
          method: 'GET' as any,
          url: resolveComWixpressCritiqueReviewsReviewsUrl({
            protoPath: '/v1/reviews/{reviewId}',
            data: payload,
            host,
          }),
          params: toURLSearchParams(payload),
        },
      ],
    };

    return metadata;
  }

  return __getReview;
}

/**
 * Creates a new review using a contact ID.
 *
 * This method requires a contact ID, see the [Contacts API](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/introduction).
 *
 * After a review is created, it may require moderation, see the introduction for the Reviews API for more information about moderation.
 *
 * If the `authorName` field is empty, and the review author:
 * - is a site member, their member name is used.
 * - isn't a site member, the field is set to `null`.
 */
export function createReview(payload: object): RequestOptionsFactory<any> {
  function __createReview({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'review.createdDate' },
          { path: 'review.updatedDate' },
          { path: 'review.reviewDate' },
          { path: 'review.reply.createdDate' },
          { path: 'review.reply.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'review.relevanceScore' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.CreateReview',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/reviews',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'review.createdDate' },
              { path: 'review.updatedDate' },
              { path: 'review.reviewDate' },
              { path: 'review.reply.createdDate' },
              { path: 'review.reply.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'review.relevanceScore' }],
          },
        ]),
    };

    return metadata;
  }

  return __createReview;
}

/**
 * Creates multiple reviews.
 *
 * If the `authorName` field is empty, and the review author:
 * - is a site member, their member name is used.
 * - isn't a site member, the field is set to `null`.
 *
 * To create a single review, call Create Review.
 */
export function bulkCreateReview(payload: object): RequestOptionsFactory<any> {
  function __bulkCreateReview({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'reviews.createdDate' },
          { path: 'reviews.updatedDate' },
          { path: 'reviews.reviewDate' },
          { path: 'reviews.reply.createdDate' },
          { path: 'reviews.reply.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'reviews.relevanceScore' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.BulkCreateReview',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/bulk/reviews/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.review.createdDate' },
              { path: 'results.review.updatedDate' },
              { path: 'results.review.reviewDate' },
              { path: 'results.review.reply.createdDate' },
              { path: 'results.review.reply.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'results.review.relevanceScore' }],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateReview;
}

/**
 * Updates a review.
 *
 * Each time the review is updated, `revision` increments by 1.
 * The existing `revision` must be included when updating the review.
 * This ensures you're working with the latest review information, and it prevents unintended overwrites.
 */
export function updateReview(payload: object): RequestOptionsFactory<any> {
  function __updateReview({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'review.createdDate' },
          { path: 'review.updatedDate' },
          { path: 'review.reviewDate' },
          { path: 'review.reply.createdDate' },
          { path: 'review.reply.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'review.relevanceScore' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.UpdateReview',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/reviews/{review.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'review.createdDate' },
              { path: 'review.updatedDate' },
              { path: 'review.reviewDate' },
              { path: 'review.reply.createdDate' },
              { path: 'review.reply.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'review.relevanceScore' }],
          },
        ]),
    };

    return metadata;
  }

  return __updateReview;
}

/** Deletes a review. */
export function deleteReview(payload: object): RequestOptionsFactory<any> {
  function __deleteReview({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.DeleteReview',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/reviews/{reviewId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'review.createdDate' },
              { path: 'review.updatedDate' },
              { path: 'review.reviewDate' },
              { path: 'review.reply.createdDate' },
              { path: 'review.reply.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'review.relevanceScore' }],
          },
        ]),
    };

    return metadata;
  }

  return __deleteReview;
}

/**
 * Deletes multiple reviews.
 *
 * All reviews that meet the specified `filter` criteria are deleted.
 *
 * To delete a single review, call Delete Review.
 */
export function bulkDeleteReviews(payload: object): RequestOptionsFactory<any> {
  function __bulkDeleteReviews({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.BulkDeleteReviews',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/bulk/reviews/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteReviews;
}

/**
 * Creates a query to retrieve a list of reviews.
 *
 *
 * The `queryReviews()` method builds a query to retrieve a list of reviews and returns a `ReviewsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `ReviewsQueryBuilder` methods onto the query.
 *
 * `ReviewsQueryBuilder` methods enable you to sort, filter,
 * and control the results that `queryReviews()` returns.
 *
 * The `queryReviews()` method runs with the following defaults, which you can override:
 *
 * - `ascending("_createdDate")`
 * - `limit(100)`
 *
 * The methods that are chained to `queryReviews()` are applied in the order they're called. For example, if you apply `descending("_createdDate")` and then `descending("content.rating")`, the results are sorted first by the `_createdDate`, and then, if there are multiple results with the same `_createdDate`, the items are sorted by `content.rating`.
 */
export function queryReviews(payload: object): RequestOptionsFactory<any> {
  function __queryReviews({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.QueryReviews',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/reviews/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.createdDate' },
              { path: 'results.updatedDate' },
              { path: 'results.reviewDate' },
              { path: 'results.reply.createdDate' },
              { path: 'results.reply.updatedDate' },
              { path: 'reviews.createdDate' },
              { path: 'reviews.updatedDate' },
              { path: 'reviews.reviewDate' },
              { path: 'reviews.reply.createdDate' },
              { path: 'reviews.reply.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.relevanceScore' },
              { path: 'reviews.relevanceScore' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryReviews;
}

/**
 * Sets a reply on a review.
 *
 * Only 1 reply can be left on a review. Calling Set Reply when a reply
 * already exists overwrites the existing reply.
 */
export function setReply(payload: object): RequestOptionsFactory<any> {
  function __setReply({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.SetReply',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/reviews/{reviewId}/reply',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'review.createdDate' },
              { path: 'review.updatedDate' },
              { path: 'review.reviewDate' },
              { path: 'review.reply.createdDate' },
              { path: 'review.reply.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'review.relevanceScore' }],
          },
        ]),
    };

    return metadata;
  }

  return __setReply;
}

/** Removes reply from a review. */
export function removeReply(payload: object): RequestOptionsFactory<any> {
  function __removeReply({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.RemoveReply',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/reviews/{reviewId}/reply',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'review.createdDate' },
              { path: 'review.updatedDate' },
              { path: 'review.reviewDate' },
              { path: 'review.reply.createdDate' },
              { path: 'review.reply.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'review.relevanceScore' }],
          },
        ]),
    };

    return metadata;
  }

  return __removeReply;
}

/** Updates the review moderation status. */
export function updateModerationStatus(
  payload: object
): RequestOptionsFactory<any> {
  function __updateModerationStatus({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.UpdateModerationStatus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/reviews/{reviewId}/moderate',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'review.createdDate' },
              { path: 'review.updatedDate' },
              { path: 'review.reviewDate' },
              { path: 'review.reply.createdDate' },
              { path: 'review.reply.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'review.relevanceScore' }],
          },
        ]),
    };

    return metadata;
  }

  return __updateModerationStatus;
}

/**
 * Updates moderation status of multiple reviews. All reviews are updated to the same moderation status.
 *
 * To update moderation status of a single review, call Update Moderation Status.
 */
export function bulkUpdateModerationStatus(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateModerationStatus({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.critique.reviews.Reviews.BulkUpdateModerationStatus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/bulk/reviews/moderate',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateModerationStatus;
}

/**
 * Retrieves the number of reviews that match a specified filter.
 *
 * If a filter isn't passed in the request, the method returns the count of all reviews.
 */
export function countReviews(payload: object): RequestOptionsFactory<any> {
  function __countReviews({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.reviews.v1.review',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.critique.reviews.Reviews.CountReviews',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCritiqueReviewsReviewsUrl({
        protoPath: '/v1/reviews/count',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countReviews;
}
